import { refundApi } from '../refundApi';
import { RefundView, SmartEnumType } from '../types/refundTypes';
import { RefundWorklistView } from '../types/refundWorklistTypes';
import { RefundGetRequest } from '../requests/refundGetRequest';
import { RefundLookupRequest } from '../requests/refundLookupRequest';
import { RefundCreateRequest } from '../requests/refundCreateRequest';
import { RefundUpdateRequest } from '../requests/refundUpdateRequest';
import { RefundWorklistEndpointRequest } from '../requests/refundWorklistRequest';
import { RefundApprovalsByUserRequest } from '../requests/refundApprovalsByUserRequest';

const refundEndpointsInjected = refundApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get single refund by ID
    // GET /RefundGet?company=<company>&center=<center>&id=<id>
    getRefund: builder.query<RefundView, RefundGetRequest>({
      query: ({ company, center, id }) => ({
        url: 'RefundGet',
        params: { company, center, id },
      }),
      providesTags: ['Refund'],
    }),

    // Lookup refunds by search term
    // GET /refundLookup?company=<company>&center=<center>&searchTerm=<searchTerm>
    lookupRefunds: builder.query<RefundView[], RefundLookupRequest>({
      query: ({ company, center, searchTerm }) => ({
        url: 'refundLookup',
        params: { company, center, searchTerm },
      }),
      providesTags: ['RefundList'],
    }),

    // Get refunds pending approval by user
    // GET /refundApprovalsByUser?company=<company>&center=<center>&userName=<userName>
    getRefundApprovalsByUser: builder.query<RefundView[], RefundApprovalsByUserRequest>({
      query: ({ company, center, userName }) => ({
        url: 'refundApprovalsByUser',
        params: { company, center, userName },
      }),
      providesTags: (result, error, { company, center, userName }) => [
        { type: 'RefundList', id: `approvals-${company}-${center}-${userName}` },
        'RefundList',
      ],
    }),

    // Create refund
    // POST /RefundCreate
    createRefund: builder.mutation<RefundView, RefundCreateRequest>({
      query: (body) => ({
        url: 'RefundCreate',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['RefundList'],
    }),

    // Update refund
    // POST /Refund
    updateRefund: builder.mutation<RefundView, RefundUpdateRequest>({
      query: (body) => ({
        url: 'Refund',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Refund', 'RefundList', 'RefundWorklist'],
    }),

    // Get refund worklist by type
    // GET /RefundWorklists?type=<type>&center=<center>&company=<company>
    getRefundWorklist: builder.query<RefundWorklistView[], RefundWorklistEndpointRequest>({
      query: ({ type, center, company }) => ({
        url: 'RefundWorklists',
        params: { type, center, company },
      }),
      providesTags: (result, error, { type, center, company }) => [
        { type: 'RefundWorklist', id: `${type}-${center}-${company}` },
        'RefundWorklist',
      ],
    }),
  }),
  overrideExisting: false,
});

// Export enhanced API for slice extraReducers
export const refundEndpoints = refundEndpointsInjected;

export const {
  useGetRefundQuery,
  useLookupRefundsQuery,
  useGetRefundApprovalsByUserQuery,
  useCreateRefundMutation,
  useUpdateRefundMutation,
  useGetRefundWorklistQuery,
} = refundEndpointsInjected;
